/**
 * LeadtimeRealityCell — the "actual lead time" table cell (Lane 156, Track B / B4).
 *
 * Renders one lead-time signal block (the frozen shape compute_leadtime_signal
 * produces server-side: chip / direction / reason / actual_avg_lt_days /
 * po_count) as a compact cell for the Material dossier Approved-Suppliers
 * table. Display-only — every number and every classification is
 * engine-computed; this component never derives one.
 *
 * States:
 *   - no block, or block with no measured actual  -> muted em-dash, with the
 *     block's honest reason as a tooltip when present (never a fabricated
 *     value).
 *   - measured but unclassified (too few receipts / no displayed number)
 *     -> the actual in muted styling + reason tooltip (shown but honestly
 *     unclassified — the Lane-153 deck discipline).
 *   - classified -> the actual colored by direction (ALIGNED green /
 *     PADDED amber / UNDERSTATED red — the Lane-148 vocabulary) + receipt
 *     count, reason tooltip.
 */
import React from 'react';
import { colors } from './SharedComponents';

const DIRECTION_COLORS = {
  aligned: colors.success,
  padded: colors.warning,
  understated: colors.danger,
};

// ⛔ LANE 985 -- THE ROUNDING DECISION IS NOT MADE IN THIS FILE.
// `serverRounded` says the figure arrived from the ONE writer that rounds a
// lead-time day: `round_leadtime_days()` in
// backend/helpers/delivery_reliability.py. It is already a whole number, and
// this component prints it verbatim. That is what makes the material dossier,
// the deck row and the chat answer show the SAME integer -- before Lane 985
// this file's `Math.round` (half-UP) disagreed with Python's `round()`
// (half-to-EVEN) on 1,188 of 16,442 supplier x part pairs, so 62.5 read "~63d"
// here and "62d" on the deck.
//
// ⚠️ THE `Math.round` BELOW IS THE PRE-LANE-977 FALLBACK ONLY -- the 6-month
// MEAN this cell showed before the median existed, which is still a 1-dp float
// and still needs rounding for display. It is NOT a second rule: it is HALF-UP,
// the same rule the writer applies, which is one of the reasons half-up was
// chosen. SUPPLIER_PART_LEADTIME_ENABLED deletes this branch when it is on.
function formatDays(value, serverRounded) {
  if (value === null || value === undefined) return null;
  const shown = serverRounded ? Number(value) : Math.round(Number(value));
  return `~${shown}d`;
}

function LeadtimeRealityCell({ block }) {
  // ⭐ LANE 977 -- Ryan's figure (per supplier, per part, 12 months, MEDIAN)
  // when the server sent one, otherwise exactly what this cell showed before.
  //
  // The server only writes `median_lt_days` when SUPPLIER_PART_LEADTIME_ENABLED
  // is on, so with the flag off `hasMedian` is false and every branch below is
  // the pre-Lane-977 code path -- there is no second flag read in the browser
  // and no way for the two to disagree about which figure to draw.
  //
  // ⛔ THE MEDIAN IS PREFERRED EVEN WHEN `actual_avg_lt_days` IS NULL. The two
  // are measured over DIFFERENT windows (12 months vs 6), so a pair can have a
  // median and no mean. Falling through to the em-dash there would hide a
  // figure the product has.
  const hasMedian = block && block.median_lt_days !== null
    && block.median_lt_days !== undefined;
  const actual = hasMedian
    ? block.median_lt_days
    : (block ? block.actual_avg_lt_days : null);
  const reason = (block && (hasMedian ? block.median_reason : block.reason)) || '';
  // ⛔ THE CAVEAT STOPS BEING HOVER-ONLY. Lane 975 §4.2 measured
  // "only 2 received orders - too few to judge" as the most honest sentence in
  // the whole lead-time family -- and it lived in a `title` attribute, so the
  // number was bold on the page and the reason it could not be trusted was
  // invisible. It is rendered inline below, under the figure it qualifies.
  const sufficiencyNote = (block && block.sufficiency_note) || '';

  if (actual === null || actual === undefined) {
    return (
      <span
        data-testid="leadtime-cell-empty"
        title={reason}
        style={{ color: colors.textMuted }}
      >
        Not available
      </span>
    );
  }

  // The receipt count must be the one the DISPLAYED figure was measured on.
  // `po_count` is the 6-month mean's sample; the median has its own
  // (`median_po_count`) over 12 months, and on the named case they are 2 and 11.
  // Showing "(2 POs)" beside a figure computed from 11 would be a new defect of
  // exactly the kind this lane exists to remove.
  //
  // ⛔ THE CHIP COLOUR SURVIVES THE MEDIAN. A first draft set this to null
  // whenever a median was shown, which silently deleted the ALIGNED / PADDED /
  // UNDERSTATED vocabulary from the page. That is a no-op on the tenant
  // measured today (zero quoted lead times, so `direction` is never anything
  // but 'unknown') and a REAL regression for the first customer who populates
  // `material_approved_suppliers.lead_time_days`. Caught at consult.
  const directionColor = DIRECTION_COLORS[block.direction];
  const poCount = (hasMedian ? block.median_po_count : block.po_count) || 0;
  // The window rides WITH the figure. A bare "~218d actual (11 POs)" is how
  // five surfaces came to show five numbers and every one of them looked right.
  const windowLabel = (hasMedian && block.window_label) || '';
  return (
    <span data-testid="leadtime-cell" title={reason}>
      <span
        style={{
          color: directionColor || colors.textMuted,
          fontWeight: directionColor
            ? 'var(--font-weight-semibold)'
            : 'var(--font-weight-regular)',
        }}
      >
        {formatDays(actual, hasMedian)} actual
      </span>
      {poCount > 0 && (
        <span
          style={{
            color: colors.textMuted,
            fontSize: 'var(--font-size-xs)',
            marginLeft: '4px',
          }}
        >
          ({poCount} PO{poCount !== 1 ? 's' : ''})
        </span>
      )}
      {windowLabel && (
        <span
          data-testid="leadtime-cell-window"
          style={{
            display: 'block',
            color: colors.textMuted,
            fontSize: 'var(--font-size-xs)',
          }}
        >
          {windowLabel}
        </span>
      )}
      {sufficiencyNote && (
        <span
          data-testid="leadtime-cell-sufficiency"
          style={{
            display: 'block',
            color: colors.warning,
            fontSize: 'var(--font-size-xs)',
            marginTop: '2px',
          }}
        >
          {sufficiencyNote}
        </span>
      )}
    </span>
  );
}

export default LeadtimeRealityCell;
